import React, { useState } from 'react';
import { Mail, ArrowLeft, ArrowRight } from 'lucide-react';
import '../../styles/realsignal-theme.css';

interface ForgotPasswordPageProps {
    onSubmit: (email: string) => Promise<string>;
    onBack: () => void;
}

const colors = {
    background: '#0F1A24',
    backgroundLight: '#162434',
    backgroundCard: '#1C2E42',
    textPrimary: '#E6EBF0',
    textSecondary: '#A9B4C2',
    textMuted: '#7F8C9C',
    accent: '#3A6A7A',
    accentLight: '#4A8A9A',
    accentDark: '#2E4A5A',
    border: '#1C2E42',
};

export const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({ onSubmit, onBack }) => {
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const result = await onSubmit(email);
            setMessage(result);
            setIsSubmitted(true);
        } catch {
            setMessage('An error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center relative overflow-hidden"
            style={{ background: colors.background }}
        >
            {/* Texture overlay - grainy noise blended with navy background */}
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: 'url(/assets/realsignal-bg-texture.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: 0.12,
                    mixBlendMode: 'luminosity',
                    filter: 'grayscale(100%)',
                    pointerEvents: 'none',
                }}
            />
            <div className="relative z-10 w-full max-w-md px-6" style={{ maxWidth: '400px' }}>
                <div
                    style={{
                        background: colors.backgroundLight,
                        borderRadius: '1.5rem',
                        padding: '2rem',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                    }}
                >
                    <button
                        onClick={onBack}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            color: colors.textSecondary,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            marginBottom: '1.5rem',
                        }}
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Back to Sign In</span>
                    </button>

                    <h1
                        style={{
                            fontSize: '1.5rem',
                            fontWeight: 600,
                            color: colors.textPrimary,
                            marginBottom: '0.5rem',
                        }}
                    >
                        Reset Password
                    </h1>
                    <p
                        style={{
                            color: colors.textSecondary,
                            marginBottom: '1.5rem',
                        }}
                    >
                        Enter your email address and we'll send you a link to reset your password.
                    </p>

                    {isSubmitted ? (
                        <div
                            style={{
                                background: 'rgba(74, 138, 154, 0.1)',
                                border: `1px solid ${colors.accent}`,
                                borderRadius: '0.5rem',
                                padding: '1rem',
                                color: colors.textPrimary,
                            }}
                        >
                            {message}
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: '1rem' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        color: colors.textSecondary,
                                        marginBottom: '0.5rem',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    Email Address
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <Mail
                                        className="w-5 h-5"
                                        style={{
                                            position: 'absolute',
                                            left: '1rem',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: colors.textMuted,
                                        }}
                                    />
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        required
                                        style={{
                                            width: '100%',
                                            background: colors.background,
                                            border: `1px solid ${colors.border}`,
                                            borderRadius: '0.75rem',
                                            padding: '0.875rem 1rem 0.875rem 3rem',
                                            color: colors.textPrimary,
                                            fontSize: '1rem',
                                        }}
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                style={{
                                    width: '100%',
                                    background: isSubmitting
                                        ? colors.accentDark
                                        : `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
                                    color: 'white',
                                    fontWeight: 600,
                                    padding: '1rem 1.5rem',
                                    borderRadius: '1.5rem',
                                    border: `2px solid ${colors.accent}`,
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    opacity: isSubmitting ? 0.7 : 1,
                                }}
                            >
                                <span>{isSubmitting ? 'Sending...' : 'Send Reset Link'}</span>
                                {!isSubmitting && <ArrowRight className="w-5 h-5" />}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
